import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { supabase } from "../utils/supabase";
import { useImageCache } from "../hooks/useImageCache";
import MiaLogo from "./MiaLogo";

const HomePage = ({
  onNavigateToRegister,
  onNavigateToLogin,
  onAdminLogin,
  webConfig,
}) => {
  const [banners, setBanners] = useState([]);
  const [loadingBanners, setLoadingBanners] = useState(true);
  const [bannerError, setBannerError] = useState(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);

  const [logoConfig, setLogoConfig] = useState(null);
  const [loadingLogo, setLoadingLogo] = useState(true);
  const [benefits, setBenefits] = useState([]);
  const [loadingBenefits, setLoadingBenefits] = useState(true);

  // Estados para emojis flotantes aleatorios
  const [emojiPositions, setEmojiPositions] = useState([]);



  // Emojis disponibles
  const availableEmojis = ["😄", "😋", "🙌", "❤️", "😍"];

  // Posiciones disponibles
  const availablePositions = [
    // Lado izquierdo
    { left: "5%", top: "20%" },   // Arriba izquierda
    { left: "10%", top: "40%" },  // Centro-arriba izquierda
    { left: "8%", top: "60%" },   // Centro-abajo izquierda
    { left: "12%", top: "80%" },  // Abajo izquierda
    { left: "15%", top: "30%" },  // Medio-arriba izquierda
    { left: "18%", top: "70%" },  // Medio-abajo izquierda

    // Lado derecho
    { left: "85%", top: "25%" },  // Arriba derecha
    { left: "90%", top: "45%" },  // Centro-arriba derecha
    { left: "88%", top: "65%" },  // Centro-abajo derecha
    { left: "82%", top: "15%" },  // Muy arriba derecha
    { left: "92%", top: "35%" },  // Medio-arriba derecha
    { left: "87%", top: "75%" },  // Abajo derecha

    // Algunas posiciones centrales (pocas)
    { left: "45%", top: "10%" },  // Muy arriba centro
    { left: "50%", top: "85%" }   // Muy abajo centro
  ];



  // Generar posiciones aleatorias para emojis
  useEffect(() => {
    const generateRandomEmojiPositions = () => {
      const shuffledPositions = [...availablePositions].sort(
        () => Math.random() - 0.5
      );
      const shuffledEmojis = [...availableEmojis].sort(
        () => Math.random() - 0.5
      );

      const newPositions = [];
      for (let i = 0; i < 5; i++) {
        newPositions.push({
          id: i,
          emoji: shuffledEmojis[i % shuffledEmojis.length],
          position: shuffledPositions[i],
          size: ["text-2xl sm:text-3xl", "text-3xl sm:text-4xl", "text-4xl sm:text-5xl"][
            Math.floor(Math.random() * 3)
          ],
          duration: 2.5 + Math.random() * 1.5, // Entre 2.5 y 4 segundos
          delay: Math.random() * 2, // Delay aleatorio hasta 2 segundos
        });
      }
      setEmojiPositions(newPositions);
    };

    // Generar posiciones iniciales
    generateRandomEmojiPositions();

    // Cambiar posiciones cada 8 segundos
    const interval = setInterval(generateRandomEmojiPositions, 8000);

    return () => clearInterval(interval);
  }, []);

  // Cargar configuración del logo
  useEffect(() => {
    // Si webConfig ya tiene el logo, úsalo directamente
    if (webConfig && webConfig.logo && webConfig.logo.url) {
      setLogoConfig(webConfig.logo);
      setLoadingLogo(false);
    } else {
      // Si no, intenta cargarlo de la base de datos
      const fetchLogoConfig = async () => {
        setLoadingLogo(true);
        const { data, error } = await supabase
          .from("web_config")
          .select("config_value")
          .eq("config_name", "logo")
          .single();

        if (error) {
          console.error("Error fetching logo config:", error);
        } else {
          setLogoConfig(data ? data.config_value : null);
        }
        setLoadingLogo(false);
      };
      fetchLogoConfig();
    }
  }, [webConfig]);

  useEffect(() => {
    const fetchBanners = async () => {
      setLoadingBanners(true);
      const { data, error } = await supabase
        .from("banners")
        .select("*")
        .eq("is_active", true)
        .order("order_index", { ascending: true });

      if (error) {
        console.error("Error fetching banners:", error);
        setBannerError("Error al cargar los banners: " + error.message);
      } else {
        setBanners(data || []);
      }
      setLoadingBanners(false);
    };

    const fetchBenefits = async () => {
      setLoadingBenefits(true);
      try {
        const { data, error } = await supabase
          .from("benefits")
          .select("*")
          .eq("is_active", true)
          .order("created_at", { ascending: false })
          .limit(4);

        if (error) {
          console.error("Error fetching benefits:", error);
          setBenefits(getDefaultBenefits());
        } else {
          if (data && data.length > 0) {
            setBenefits(data);
          } else {
            setBenefits(getDefaultBenefits());
          }
        }
      } catch (error) {
        console.error("Error fetching benefits:", error);
        setBenefits(getDefaultBenefits());
      }
      setLoadingBenefits(false);
    };

    fetchBanners();
    fetchBenefits();
  }, []);

  useEffect(() => {
    if (banners.length > 1) {
      const interval = setInterval(() => {
        setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % banners.length);
      }, 5000);

      return () => clearInterval(interval);
    }
  }, [banners]);

  // Extract image URLs for caching
  const allImageUrls = [
    ...banners.map(banner => banner.image_url).filter(Boolean),
    ...benefits.map(benefit => benefit.image_url).filter(Boolean)
  ];

  // Use image cache hook
  const { getCachedUrl } = useImageCache(allImageUrls, {
    preloadOnMount: true,
    logProgress: true
  });

  // Use image cache to avoid repeated downloads
  const getImageUrl = (imageUrl) => {
    return getCachedUrl(imageUrl);
  };

  const getDefaultBenefits = () => {
    return [
      {
        id: "default-1",
        title: "Más de 50 promos exclusivas",
        description:
          "Accede a descuentos y promociones solo para miembros del club.",
        image_url: "/ICONOS%20BENEFICIOS-01.svg",
      },
      {
        id: "default-2",
        title: "Sorteos y premios semanales",
        description:
          "Participa en sorteos exclusivos con premios increíbles cada semana.",
        image_url: "/ICONOS%20BENEFICIOS-02.svg",
      },
      {
        id: "default-3",
        title: "Degustación de lanzamientos",
        description:
          "Sé el primero en probar nuestros nuevos productos antes que nadie.",
        image_url: "/ICONOS%20BENEFICIOS-03.svg",
      },
      {
        id: "default-4",
        title: "Obsequios de cumpleaños",
        description:
          "Recibe sorpresas especiales en tu día más importante del año.",
        image_url: "/ICONOS%20BENEFICIOS-04.svg",
      },
    ];
  };

  return (
    <motion.div
      className="flex flex-col items-center justify-center min-h-screen text-center px-3 sm:px-4 w-full max-w-full overflow-hidden pt-2 sm:pt-8 mt-2 sm:mt-16 pb-16 sm:pb-20 relative z-10"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1 }}
    >
      {/* Emojis flotantes aleatorios */}
      <div className="fixed inset-0 z-50 pointer-events-none overflow-hidden">
        {emojiPositions.map((emojiData) => (
          <motion.div
            key={`${emojiData.id}-${emojiData.emoji}-${emojiData.position.left}`}
            className={`absolute ${emojiData.size}`}
            initial={{ opacity: 0, scale: 0, rotate: 0 }}
            animate={{
              opacity: [0, 1, 1, 1, 0],
              scale: [0, 1.2, 1, 1, 0],
              rotate: [0, 180, 360, 540, 720]
            }}
            transition={{
              duration: emojiData.duration,
              repeat: Infinity,
              ease: "easeInOut",
              delay: emojiData.delay,
              repeatDelay: 0.5
            }}
            style={emojiData.position}
          >
            {emojiData.emoji}
          </motion.div>
        ))}
      </div>
      <motion.h1
        className="text-3xl sm:text-5xl md:text-5xl font-extrabold text-gray-900 mb-4 sm:mb-6 leading-tight flex flex-col flex-wrap items-center justify-center gap-2 sm:gap-4"
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        <span>Bienvenido a</span>
        <span className="flex items-center justify-center mb-8">
          {loadingLogo ? (
            <div className="w-44 h-44 sm:w-44 sm:h-44 md:w-48 md:h-48 lg:w-52 lg:h-52 bg-gray-200 rounded-full animate-pulse"></div>
          ) : (
            <div className="relative">
              <div>
                <MiaLogo
                  width="auto"
                  height="auto"
                  className="h-44 sm:h-44 md:h-48 lg:h-52 w-auto"
                />
              </div>
            </div>
          )}
        </span>
      </motion.h1>
      {/* Carousel de Banners */}
      {loadingBanners ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-gray-600 mb-8"
        >
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mr-3"></div>
            <p>Cargando banners...</p>
          </div>
        </motion.div>
      ) : bannerError ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-red-600 mb-8"
        >
          {bannerError}
        </motion.div>
      ) : banners.length > 0 ? (
        <motion.div
          className="w-full max-w-4xl mb-8 overflow-hidden rounded-2xl shadow-xl relative z-0 h-48 sm:h-56 md:h-64 lg:h-72"
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
        >
          <AnimatePresence initial={false} mode="wait">
            {banners.map(
              (banner, index) =>
                index === currentBannerIndex && (
                  <motion.div
                    key={banner.id}
                    className="absolute top-0 left-0 w-full h-full bg-cover bg-center flex items-center justify-center text-white"
                    style={{ backgroundImage: `url(${banner.image_url})` }}
                    initial={{ opacity: 0, x: 50 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -50 }}
                    transition={{ duration: 0.6 }}
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-black/50 via-black/30 to-black/50 flex flex-col items-center justify-center p-6">
                      <h2 className="text-2xl md:text-4xl font-extrabold mb-3 text-center drop-shadow-lg">
                        {banner.title}
                      </h2>
                      {banner.subtitle && (
                        <p className="text-base md:text-lg mb-4 text-center max-w-2xl drop-shadow-md">
                          {banner.subtitle}
                        </p>
                      )}
                      {banner.link_url && (
                        <motion.a
                          href={banner.link_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-white/90 backdrop-blur-sm text-gray-800 px-6 py-2 rounded-full font-semibold hover:bg-white transition-all duration-300 shadow-lg text-sm"
                          whileHover={{ scale: 1.05 }}
                          whileTap={{ scale: 0.95 }}
                        >
                          Ver más
                        </motion.a>
                      )}
                    </div>
                  </motion.div>
                )
            )}
          </AnimatePresence>
        </motion.div>
      ) : null}

      <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 sm:mb-8 w-full max-w-md">
        <motion.button
          onClick={onNavigateToRegister}
          className="text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3"
          style={{
            backgroundColor: "#9e187e",
            backgroundImage:
              "linear-gradient(135deg, #9e187e 0%, #7d1463 100%)",
          }}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            delay: 1.2,
            duration: 0.6,
            type: "spring",
            stiffness: 200,
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <span className="hidden sm:inline">Únete al Club Ahora</span>
          <span className="sm:hidden">Únete Ahora</span>
          <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
        </motion.button>

        <motion.button
          onClick={onNavigateToLogin}
          className="bg-white px-6 sm:px-8 py-3 sm:py-4 rounded-full font-bold text-base sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2 sm:gap-3"
          style={{
            color: "#9e187e",
            borderColor: "#9e187e",
            border: "2px solid #9e187e",
          }}
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{
            delay: 1.4,
            duration: 0.6,
            type: "spring",
            stiffness: 200,
          }}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          Iniciar Sesión
        </motion.button>
      </div>

      {/* Benefits dinámicos desde la base de datos */}
      {loadingBenefits ? (
        <motion.div
          className="flex justify-center items-center py-12"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
        >
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mr-3"></div>
          <p className="text-gray-600">Cargando beneficios...</p>
        </motion.div>
      ) : (
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 max-w-6xl w-full px-3 sm:px-0"
          initial={{ y: 50, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 1.6, duration: 0.8 }}
        >
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.id}
              className="bg-white/90 backdrop-blur-md p-4 sm:p-6 rounded-2xl shadow-lg flex flex-col items-center hover:-translate-y-1 hover:shadow-xl transition-all duration-300 cursor-pointer border border-gray-200/50"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 1.6 + index * 0.1, duration: 0.6 }}
            >
              <img
                src={getImageUrl(benefit.image_url)}
                alt={benefit.title}
                className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4"
                onError={(e) => {
                  console.log("Error loading image:", e.target.src);
                  // Use fallback image without timestamp to allow caching
                  const fallbackUrl =
                    benefit.image_url?.replace("%20", " ") ||
                    "/ICONOS BENEFICIOS-01.svg";
                  e.target.src = getCachedUrl(fallbackUrl);
                }}
                onLoad={(e) => {
                  console.log("Image loaded successfully:", e.target.src);
                }}
              />
              <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-2 text-center">
                {benefit.title}
              </h3>
              <p className="hidden lg:block text-gray-600 text-center text-xs sm:text-sm leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      )}
    </motion.div>
  );
};

export default HomePage;
